import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { PointHistoryApi } from 'api';
import type { PointHistory } from 'api/methods/PointHistoryApi';

/**
 * [PointHistory] Query key 생성
 * --
 */
export const pointHistoryQueryKeys = createQueryKeys('point_history', {
  getPointHistory: (
    idx: number,
    params?: { [key: string]: string | number }
  ) => [idx, params],
  getPointHistories: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [PointHistory] 포인트 적립 내역 전체 조회
 * --
 */
export const useGetPointHistories = (
  params?: {
    [key: string]: string | number;
  },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: pointHistoryQueryKeys.getPointHistories(params).queryKey,
    queryFn: () => PointHistoryApi.getPointHistories(params),
    ...options,
  });
  return data;
};

/**
 * [PointHistory] 포인트 적립 내역 상세 조회
 * --
 */
export const useGetPointHistory = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: pointHistoryQueryKeys.getPointHistory(idx, params).queryKey,
    queryFn: () => PointHistoryApi.getPointHistory(idx, params),
  });

  return data;
};

/**
 * [PointHistory] 포인트 적립 내역 생성
 * --
 */
export const useCreatePointHistory = (body: PointHistory) => {
  const { mutateAsync: createPointHistory, ...result } = useMutation({
    mutationFn: (variables: PointHistory) =>
      PointHistoryApi.createPointHistory(variables),
  });

  return { createPointHistory, ...result };
};

/**
 * [PointHistory] 포인트 적립 내역 수정
 * --
 */
export const useUpdatePointHistory = (body: PointHistory) => {
  const { mutateAsync: updatePointHistory, ...result } = useMutation({
    mutationFn: (variables: PointHistory) =>
      PointHistoryApi.updatePointHistory(variables),
  });

  return { updatePointHistory, ...result };
};

/**
 * [PointHistory] 포인트 적립 내역 삭제
 * --
 */
export const useDeletePointHistory = (idx: number) => {
  const { mutateAsync: deletePointHistory, ...result } = useMutation({
    mutationFn: (variables: number) =>
      PointHistoryApi.deletePointHistory(variables),
  });

  return { deletePointHistory, ...result };
};

/**
 * [PointHistory] 포인트 적립 내역 수정
 * --
 */
export const useUpsertPointHistory = () => {
  const { mutateAsync: upsertPointHistory, ...result } = useMutation({
    mutationFn: (variables: PointHistory[]) =>
      PointHistoryApi.upsertPointHistory(variables),
  });

  return { upsertPointHistory, ...result };
};

/**
 * [PointHistory] 포인트 적립 사용처리
 * --
 */
export const useUsePointHistory = () => {
  const { mutateAsync: usedPointHistory, ...result } = useMutation({
    mutationFn: (variables: PointHistory) =>
      PointHistoryApi.usedPointHistory(variables),
  });

  return { usedPointHistory, ...result };
};
